/**
 * Sanitization utilities for safe display of user/backend content
 */

const MAX_ERROR_DISPLAY_LENGTH = 120

/**
 * Sanitize error message for safe display in UI (toasts, alerts).
 * Truncates and strips HTML/control chars to reduce XSS risk and layout issues.
 */
export function sanitizeErrorForDisplay(
  message: string,
  maxLength: number = MAX_ERROR_DISPLAY_LENGTH
): string {
  if (!message || typeof message !== 'string') return ''
  const stripped = message
    .replace(/<[^>]*>/g, '')
    .replace(/[\x00-\x1f\x7f]/g, '')
    .trim()
  return stripped.length <= maxLength
    ? stripped
    : stripped.slice(0, maxLength) + '…'
}

/**
 * Allow only https:, http:, or path-only URLs for use in CSS (e.g. background-image).
 * Rejects javascript:, data:, and other schemes to prevent XSS when URL comes from API/CMS.
 */
export function isSafeUrlForCss(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false
  const trimmed = url.trim()
  if (!trimmed) return false
  if (trimmed.startsWith('https://') || trimmed.startsWith('http://'))
    return true
  if (trimmed.startsWith('/') && !trimmed.includes('//')) return true
  return false
}

/** Format failed upload items for toast display (e.g. "a.jpg: err1; b.jpg: err2 and 1 more"). Sanitizes filename and error for safe display. */
export function formatFailedForDisplay(
  failed: Array<{ filename: string; error: string }>,
  maxShown = 3
): string {
  const list = failed
    .slice(0, maxShown)
    .map(
      (f) =>
        `${sanitizeErrorForDisplay(f.filename)}: ${sanitizeErrorForDisplay(f.error)}`
    )
    .join('; ')
  const more =
    failed.length > maxShown ? ` and ${failed.length - maxShown} more` : ''
  return `${list}${more}`
}
